/**
 * Client-side partnership computation. Mirrors backend/partnerships.py.
 *
 * Used by:
 *  - member/Stats.jsx (read-only match view)
 *  - public landing widget (best partnerships)
 *
 * Given an ``innings`` dict (``batting`` list, ``fall_of_wickets`` list,
 * ``total_runs``, ``total_wickets``), returns the ordered partnership rows.
 */
export function computePartnerships(innings) {
  const batting = [...(innings?.batting || [])].sort(
    (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)
  );
  const fow = [...(innings?.fall_of_wickets || [])]
    .filter((f) => f && f.wicket_number)
    .sort((a, b) => a.wicket_number - b.wicket_number);
  const totalRuns = Number(innings?.total_runs || 0);
  const totalWickets = Number(innings?.total_wickets || 0);

  if (batting.length < 2) return [];

  const byOrder = {};
  batting.forEach((b, i) => {
    const o = Number(b.order) || i + 1;
    byOrder[o] = b;
  });
  const orders = Object.keys(byOrder).map(Number).sort((a, b) => a - b);
  const scoreAtFall = (runs, wickets) =>
    wickets >= 10 || (totalWickets >= 10 && wickets === totalWickets)
      ? `${runs} all out`
      : `${runs}/${wickets}`;
  const key = (b) => ({
    id: b?.player_id || null,
    name: (b?.player_name || "").trim() || "Unknown",
    order: Number(b?.order) || 0,
  });

  if (fow.length === 0) {
    return [
      {
        wicket_number: 1,
        batters: [key(byOrder[orders[0]]), key(byOrder[orders[1]])],
        runs: totalRuns,
        score_at_fall: scoreAtFall(totalRuns, totalWickets),
        unfinished: totalWickets < 10,
      },
    ];
  }

  let pair = [orders[0], orders[1]];
  let incomingIdx = 2;
  let prevRuns = 0;
  const rows = [];
  for (const f of fow) {
    const runsAt = Number(f.runs_at_fall) || 0;
    const partnershipRuns = Math.max(runsAt - prevRuns, 0);
    let dismissedOrder = null;
    const wantId = f.dismissed_batter_id;
    const wantName = (f.dismissed_batter_name || "").trim().toLowerCase();
    for (const o of pair) {
      const b = byOrder[o];
      if (!b) continue;
      if (wantId && (b.player_id || b.id) === wantId) {
        dismissedOrder = o;
        break;
      }
      if (wantName && (b.player_name || "").trim().toLowerCase() === wantName) {
        dismissedOrder = o;
        break;
      }
    }
    if (!dismissedOrder) dismissedOrder = Math.min(...pair);
    const survivingOrder = pair[0] === dismissedOrder ? pair[1] : pair[0];
    const pairBatters = [key(byOrder[pair[0]]), key(byOrder[pair[1]])].sort(
      (a, b) => a.order - b.order
    );
    rows.push({
      wicket_number: Number(f.wicket_number),
      batters: pairBatters,
      runs: partnershipRuns,
      score_at_fall: scoreAtFall(runsAt, Number(f.wicket_number)),
      unfinished: false,
    });
    prevRuns = runsAt;
    if (incomingIdx < orders.length) {
      pair = [survivingOrder, orders[incomingIdx]];
      incomingIdx += 1;
    } else {
      pair = null;
      break;
    }
  }
  if (pair && totalRuns > prevRuns) {
    const trailing = totalRuns - prevRuns;
    const pairBatters = [key(byOrder[pair[0]]), key(byOrder[pair[1]])].sort(
      (a, b) => a.order - b.order
    );
    rows.push({
      wicket_number: fow.length + 1,
      batters: pairBatters,
      runs: trailing,
      score_at_fall: scoreAtFall(totalRuns, fow.length),
      unfinished: true,
    });
  }
  return rows;
}

export function bestPartnerships(partnerships, topN = 5) {
  return [...(partnerships || [])].sort((a, b) => b.runs - a.runs).slice(0, topN);
}
